import React from 'react';
import partial from 'lodash/partial';
import {Link} from 'react-router-dom';
import includes from 'lodash/includes';
import {Button, Icon} from 'semantic-ui-react';
import {FormattedMessage} from 'react-intl';
import {requiresW9AndAffidavit} from 'pages/EditPrize/utils';
import WinnerStatus from 'components/prizes/WinnerStatus';
import PrizeBreakId from 'components/prizes/PrizeBreakId';
import SponsoredWinnerAuditing from './SponsoredWinnerAuditing';
import AutoSaveFeaturedCheckbox from './AutoSaveFeaturedCheckbox';
import AutoSaveDateOrderedInput from './AutoSaveDateOrderedInput';
import {formatDate, EmptyFunc} from 'utils/DBUtils';
import awardIconUri from 'icons/award.svg';
import {isFeatured} from './utils';
import {AutoSaveTextInput} from "../../components/forms/AutoSaveFormFields";

export default {
  Winner: props => ({
    header: <FormattedMessage id='winnersList.column.header.winner' />,
    fieldName: 'lastName',
    detailFunc: winner => {
      const onClick = event => {
        event.preventDefault();
        props.openWinnerDetailModal(winner.id);
      };

      return (
        <a href='#' onClick={onClick}>
          {[winner.firstName, winner.lastName].join(' ')}
        </a>
      );
    },
    subRowDetailFunc: winner => <FormattedMessage id='winnersList.column.subrow.winner.sponsored' />,
    canSort: true
  }),

  Prize: props => ({
    header: <FormattedMessage id='winnersList.column.header.prize' />,
    fieldName: 'prizeName',
    detailFunc: winner => {
      if (!winner.prize) {
        return null;
      }

      const allowSubRow = requiresW9AndAffidavit(winner.prize);
      const isExpanded = props.currentExpandedRow === winner.id;
      const toggleSubRow = partial(props[isExpanded ? 'collapseRow' : 'expandRow'], winner.id);

      return (
        <span className='prize-title'>
          {allowSubRow && <Icon name='flag' onClick={toggleSubRow} />}
          <Link to={`/prize/${winner.prize.id}/edit?openSection=editPrizeSection`}>
            {winner.prize.title}
          </Link>
        </span>
      );
    },
    subRowDetailFunc: winner => <SponsoredWinnerAuditing {...winner} />,
    canSort: true
  }),

  Status: props => ({
    header: <FormattedMessage id='winnersList.column.header.status' />,
    fieldName: 'status',
    detailFunc: winner => <WinnerStatus {...winner} />,
    subRowDetailFunc: EmptyFunc,
    canSort: true
  }),

  Claimed: props => ({
    header: <FormattedMessage id='winnersList.column.header.claimed' />,
    fieldName: 'claimedOn',
    detailFunc: winner => winner.claimedOn && formatDate(winner.claimedOn, props.winnersTableConfig.dateFormat),
    subRowDetailFunc: EmptyFunc,
    canSort: true
  }),

  Ordered: props => ({
    header: <FormattedMessage id='winnersList.column.header.ordered' />,
    fieldName: 'dateOrdered',
    detailFunc: winner => winner.prize && (
      <AutoSaveDateOrderedInput
        value={winner.dateOrdered}
        disabled={requiresW9AndAffidavit(winner.prize)}
        onChange={partial(props.saveDateOrdered, winner.id)}
        saveError={includes(props.dateOrderedSaveErrors, winner.id)}
        isSaving={includes(props.dateOrderedPendingSaves, winner.id)}
        saveSuccess={includes(props.dateOrderedSaveSuccesses, winner.id)} />
    ),
    subRowDetailFunc: EmptyFunc,
    canSort: true
  }),

  Notes: props => ({
    header: <FormattedMessage id='winnersList.column.header.fulfillmentNotes' />,
    fieldName: 'fulfillmentNotes',
    detailFunc: winner => (
        <Button primary size="mini" onClick={partial(props.startEditNotes, winner.id)}>Edit</Button>
    ),
    subRowDetailFunc: EmptyFunc,
    canSort: false
  }),

  Notified: props => ({
    header: <FormattedMessage id='winnersList.column.header.notified' />,
    fieldName: 'dateContacted',
    detailFunc: winner => winner.dateContacted && formatDate(winner.dateContacted, props.winnersTableConfig.dateFormat),
    subRowDetailFunc: EmptyFunc,
    canSort: true
  }),

  Renotified: props => ({
    header: <FormattedMessage id='winnersList.column.header.renotified' />,
    fieldName: 'renotifiedDate',
    detailFunc: winner => winner.renotified && formatDate(winner.renotified, props.winnersTableConfig.dateFormat),
    subRowDetailFunc: EmptyFunc,
    canSort: true
  }),

  Forfeited: props => ({
    header: <FormattedMessage id='winnersList.column.header.forfeited' />,
    fieldName: 'forfeitDate',
    detailFunc: winner => winner.forfeited && formatDate(winner.forfeited, props.winnersTableConfig.dateFormat),
    subRowDetailFunc: EmptyFunc,
    canSort: true
  }),

  PrizeLink: props => ({
    header: <FormattedMessage id='winnersList.column.header.prizeLink' />,
    detailFunc: winner => winner.prize && (
      <a
        rel='noopener'
        target='_blank'
        className='winner-prize-link'
        href={winner.prize.orderLink}>
        <img src={awardIconUri} />
      </a>
    ),
    subRowDetailFunc: EmptyFunc
  }),

  EditStory: props => ({
    header: <FormattedMessage id='winnersList.column.header.editStory' />,
    detailFunc: winner => winner.winnerStory && (
      <Icon
        name='edit'
        size='large'
        className='winner-edit-story-icon'
        onClick={partial(props.openEditWinnerStoryModal, winner.id)} />
    ),
    subRowDetailFunc: EmptyFunc
  }),

  FeaturedToggle: props => ({
    header: <FormattedMessage id='winnersList.column.header.makeFeatured' />,
    detailFunc: winner => {
      if (winner.winnerStory) {
        const saveError = includes(props.featuredWinnerSaveErrors, winner.id);
        const isSaving = includes(props.featuredWinnerPendingSaves, winner.id);
        const saveSuccess = includes(props.featuredWinnerSaveSuccesses, winner.id);
        const checked = isSaving ? false : isFeatured(winner);

        return (
          <AutoSaveFeaturedCheckbox
            checked={checked}
            isSaving={isSaving}
            saveError={saveError}
            saveSuccess={saveSuccess}
            onChange={partial(props.toggleFeaturedWinner, winner.id)} />
        );
      }
    },
    subRowDetailFunc: EmptyFunc
  }),

  BreakId: props => ({
    header: <FormattedMessage id='winnersList.column.header.breakId' />,
    detailFunc: winner => (
      <PrizeBreakId
        prize={winner.prize}
        onClick={props.openPrizeChallengesModal} />
    ),
    subRowDetailFunc: EmptyFunc
  })
};



// WEBPACK FOOTER //
// ./public/pages/ListWinners/columns.jsx